import React from 'react';
import { useLinkedCustomers } from '@api/hooks/useVendorPortal';
import { Building2 } from 'lucide-react';
import type { LinkedCustomerResponse } from '@schemas/api';

const STATUS_LABELS: Record<string, string> = {
  pending_invitation: 'Pending',
  registered: 'Registered',
  active: 'Active',
  deactivated: 'Deactivated',
};

const STATUS_COLORS: Record<string, string> = {
  pending_invitation: 'bg-yellow-100 text-yellow-800',
  registered: 'bg-blue-100 text-blue-800',
  active: 'bg-green-100 text-green-800',
  deactivated: 'bg-gray-100 text-gray-600',
};

export default function VendorCustomersPage() {
  const { data, isLoading } = useLinkedCustomers();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-content-primary dark:text-content-primary-dark">
          Connected Customers
        </h1>
        <p className="mt-1 text-content-secondary dark:text-content-secondary-dark">
          Customers who have linked your vendor account.
        </p>
      </div>

      {isLoading ? (
        <div className="text-gray-500 p-6">Loading customers...</div>
      ) : !data?.items.length ? (
        <div className="text-center py-12 bg-white dark:bg-surface-base-dark rounded-lg border border-border-default">
          <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">No customers connected yet.</p>
          <p className="text-sm text-gray-500 mt-1">
            When a customer invites you, they will appear here.
          </p>
        </div>
      ) : (
        <div className="bg-white dark:bg-surface-base-dark rounded-lg border border-border-default overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Linked Since</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {data.items.map((customer: LinkedCustomerResponse) => (
                <tr key={customer.link_id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <Building2 className="h-4 w-4 text-blue-600" />
                      </div>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {customer.customer_name}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[customer.status] || 'bg-gray-100 text-gray-600'}`}>
                      {STATUS_LABELS[customer.status] || customer.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {new Date(customer.linked_since).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 text-sm text-gray-500">
            {data.total} customer{data.total !== 1 ? 's' : ''}
          </div>
        </div>
      )}
    </div>
  );
}
